import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { obterConta, obterEmpresa, obterConfigEmpresa, salvarEmpresaPerfil, enviarLogoEmpresa, empresaLogoUrl } from "../services/api";

const PORTES = ["Micro", "Pequena", "Média", "Grande"];

/**
 * Página pública da empresa: setor, cidade, porte, sobre, certificações, e-mail de candidaturas e logo.
 *
 * Vive num componente próprio porque é editado em DOIS lugares — em /conta, junto dos dados pessoais de quem
 * está logado, e no painel, que é onde a empresa vem quando quer configurar alguma coisa. Duplicar o formulário
 * garantiria que um dos dois ficasse para trás na primeira mudança de campo.
 *
 * Carrega e salva por conta própria: no painel não existe um botão "Salvar" geral para carregar junto, e
 * depender de um botão que mora em outra tela é como um campo silenciosamente para de ser gravado.
 */
export default function DadosEmpresa({ titulo = "Dados da empresa" }) {
  const [emp, setEmp] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoV, setLogoV] = useState(0);   // cache-buster: sem isto o browser mostra o logo antigo
  const [msg, setMsg] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const c = await obterConta();
        if (c.tipo !== 1 || !c.empresaId) { setEmp(null); return; }
        const e = await obterEmpresa(c.empresaId);
        // O e-mail que recebe candidaturas é dado operacional e não vem no DTO público: busca à parte, e
        // falhar aqui não pode derrubar o formulário inteiro.
        let cfg = {};
        try { cfg = await obterConfigEmpresa(); } catch { /* segue sem o campo preenchido */ }
        setEmp({
          id: c.empresaId, nome: e.empresa.nome, verificada: e.empresa.verificada, temLogo: e.empresa.temLogo,
          setor: e.empresa.setor || "", cidade: e.empresa.cidade || "", sobre: e.empresa.sobre || "",
          porte: e.empresa.porte || "", certificacoes: (e.empresa.certificacoes || []).join(", "),
          emailCandidaturas: cfg.emailCandidaturas || "",
          whatsappCandidaturas: cfg.whatsappCandidaturas || "",
        });
      } catch (e) { setErro(e.message); } finally { setCarregando(false); }
    })();
  }, []);

  async function salvar() {
    setMsg(""); setErro(""); setSalvando(true);
    try {
      await salvarEmpresaPerfil({
        setor: emp.setor, cidade: emp.cidade, sobre: emp.sobre, porte: emp.porte,
        certificacoes: emp.certificacoes, emailCandidaturas: emp.emailCandidaturas,
        whatsappCandidaturas: emp.whatsappCandidaturas,
      });
      setMsg("Dados salvos.");
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  }

  async function enviarLogo(file) {
    if (!file) return;
    setMsg(""); setErro(""); setLogoBusy(true);
    try {
      await enviarLogoEmpresa(file);
      setEmp((x) => ({ ...x, temLogo: true }));
      setLogoV((v) => v + 1);
      setMsg("Logo atualizado.");
    } catch (e) { setErro(e.message); } finally { setLogoBusy(false); }
  }

  if (carregando) return null;
  if (!emp) return null;

  const inp = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none";
  const lab = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-semibold text-text mb-1">{titulo} — {emp.nome}</h2>
      <p className="text-sm text-gray-600 mb-4">É o que o candidato vê na sua página e nas suas vagas.</p>

      {!emp.verificada && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          ⏳ Empresa em análise — os dados da página pública só poderão ser salvos após a aprovação do eaiRH.
        </p>
      )}
      {msg && <p className="mb-3 text-sm text-green-700">{msg}</p>}
      {erro && <p role="alert" className="mb-3 text-sm text-red-600">{erro}</p>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label className={lab}>Setor</label>
          <input className={inp} aria-label="Setor da empresa" value={emp.setor}
            onChange={(e) => setEmp({ ...emp, setor: e.target.value })} placeholder="Ex: Tecnologia" /></div>
        <div><label className={lab}>Cidade</label>
          <input className={inp} aria-label="Cidade da empresa" value={emp.cidade}
            onChange={(e) => setEmp({ ...emp, cidade: e.target.value })} placeholder="São Paulo" /></div>
        <div><label className={lab}>Porte</label>
          <select className={inp} aria-label="Porte da empresa" value={emp.porte}
            onChange={(e) => setEmp({ ...emp, porte: e.target.value })}>
            <option value="">Selecione</option>{PORTES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></div>
      </div>

      <div className="mt-3"><label className={lab}>Sobre a empresa</label>
        <textarea className={inp} aria-label="Sobre a empresa" rows="3" value={emp.sobre}
          onChange={(e) => setEmp({ ...emp, sobre: e.target.value })}
          placeholder="Descrição, área de atuação, diferenciais..." /></div>

      <div className="mt-3"><label className={lab}>Principais certificações</label>
        <input className={inp} aria-label="Principais certificações da empresa" value={emp.certificacoes}
          onChange={(e) => setEmp({ ...emp, certificacoes: e.target.value })} placeholder="GPTW 2025, GPMH, ISO 27001" />
        <p className="text-xs text-gray-400 mt-1">
          Separe por vírgula (até 5). Aparecem na sua página como <em>informado pela empresa</em> — não alteram
          sua nota, que vem dos relatos dos candidatos.
        </p></div>

      {/* Quem cadastra a empresa quase nunca é quem cuida do processo seletivo — sem isto, o aviso de
          candidatura chegava a uma caixa que ninguém abre. */}
      <div className="mt-3"><label className={lab}>E-mail que recebe as candidaturas</label>
        <input className={inp} type="email" aria-label="E-mail que recebe as candidaturas" value={emp.emailCandidaturas}
          onChange={(e) => setEmp({ ...emp, emailCandidaturas: e.target.value })} placeholder="recrutamento@suaempresa.com.br" />
        <p className="text-xs text-gray-400 mt-1">
          A cada nova candidatura, avisamos esse endereço com o nome, a mensagem e a pretensão salarial do
          candidato. Em branco, o aviso vai para o e-mail desta conta.
        </p></div>

      {/* Terceira via de entrega: existe para a empresa sem ATS cujo único e-mail é uma caixa compartilhada.
          A candidatura chega no celular de uma PESSOA, com nome e link do currículo. */}
      <div className="mt-3"><label className={lab}>WhatsApp que recebe as candidaturas</label>
        <input className={inp} aria-label="WhatsApp que recebe as candidaturas" value={emp.whatsappCandidaturas}
          onChange={(e) => setEmp({ ...emp, whatsappCandidaturas: e.target.value })} placeholder="(11) 99999-9999" />
        <p className="text-xs text-gray-400 mt-1">
          Opcional. Preencha se preferir receber cada candidatura por WhatsApp, com o nome do candidato e o link
          do currículo. Útil quando o e-mail da empresa é uma caixa compartilhada.
        </p></div>

      <div className="mt-3">
        <label className={lab}>Logotipo</label>
        <div className="flex items-center gap-3">
          {emp.temLogo && (
            <img src={`${empresaLogoUrl(emp.id)}?v=${logoV}`} alt="Logo atual"
              className="w-16 h-16 rounded-lg object-contain bg-white border border-gray-200" />
          )}
          <label className={`text-sm font-semibold rounded-lg border border-primary text-primary px-3 py-2 ${emp.verificada && !logoBusy ? "cursor-pointer hover:bg-primary hover:text-white" : "opacity-50 cursor-not-allowed"}`}>
            {logoBusy ? "Enviando..." : (emp.temLogo ? "Trocar logo" : "Enviar logo")}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden"
              disabled={!emp.verificada || logoBusy}
              onChange={(ev) => { enviarLogo(ev.target.files?.[0]); ev.target.value = ""; }} />
          </label>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          PNG, JPG, WEBP, GIF ou SVG, até 2 MB. Aparece na sua página e nos cards de vaga.
          {!emp.verificada && " Disponível após a aprovação."}
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="button" onClick={salvar} disabled={salvando || !emp.verificada}
          className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2.5 disabled:opacity-60">
          {salvando ? "Salvando..." : "Salvar"}
        </button>
        <Link to={`/empresas/${emp.id}`} className="text-sm text-primary font-semibold hover:underline">Ver página pública →</Link>
      </div>
    </section>
  );
}
